import * as zod from "zod";

/**
 * @summary Get Auto Recipe Defaults
 */
export const GetAutoRecipeDefaultsParams = zod.object({
  tenant_name: zod.string(),
  search_type: zod.enum([
    "charging",
    "charging_two_charge",
    "charging_three_charge",
    "charging_fresh_anode",
    "charging_wet",
    "charging_two_charge_wet",
    "medium_term_charging",
    "purchasing",
    "purchasing_unrestricted",
    "simulator",
  ]),
});

export const GetAutoRecipeDefaultsQueryParams = zod.object({
  scope: zod.enum(["defaults", "search"]).optional(),
});

export const GetAutoRecipeDefaultsResponse = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  effective: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  scrapchef: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  definition: zod.object({
    json_schema: zod
      .record(zod.string(), zod.unknown())
      .describe(
        "JSON Schema for editable values. Cross-field rules are checked by the server."
      ),
    sections: zod.array(
      zod.object({
        name: zod.string(),
        label: zod.string(),
        translation_key: zod.union([zod.string(), zod.null()]),
        fields: zod.array(
          zod.object({
            name: zod.string(),
            label: zod.string(),
            translation_key: zod.union([zod.string(), zod.null()]),
            unit: zod.union([
              zod
                .enum([
                  "mass",
                  "small_mass",
                  "cost",
                  "specific_cost",
                  "electrical_energy_consumption",
                  "mass_fraction",
                  "yield",
                  "volume",
                  "density",
                  "energy",
                  "specific_energy_cost",
                  "time",
                  "small_mass_fraction",
                ])
                .describe("Enum of quantities requiring different units"),
              zod.null(),
            ]),
            scopes: zod.array(zod.enum(["defaults", "search"])),
            widget: zod.enum([
              "scalar",
              "month",
              "time_intervals",
              "monthly_targets",
              "inventory_targets",
              "conditional_inventory_targets",
              "material_exclusivity",
              "material_limit_overrides",
              "steel_grades",
            ]),
            monthly_value_mode: zod.union([
              zod.enum(["independent", "carry_forward"]),
              zod.null(),
            ]),
            initial_value_from: zod.union([zod.string(), zod.null()]),
            price_material: zod.union([zod.string(), zod.null()]),
            help_text: zod.union([zod.string(), zod.null()]),
            help_translation_key: zod.union([zod.string(), zod.null()]),
          })
        ),
      })
    ),
    period_layout: zod.enum([
      "single_period",
      "deployable_sequence",
      "first_deployable_sequence",
      "schedule_forecast",
      "calendar_forecast",
      "weekly_forecast",
    ]),
  }),
});

/**
 * @summary Validate Auto Recipe Defaults
 */
export const ValidateAutoRecipeDefaultsParams = zod.object({
  tenant_name: zod.string(),
});

export const ValidateAutoRecipeDefaultsQueryParams = zod.object({
  scope: zod.enum(["defaults", "search"]).optional(),
});

export const ValidateAutoRecipeDefaultsBody = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  parameters: zod.record(zod.string(), zod.unknown()),
});

export const ValidateAutoRecipeDefaultsResponse = zod.record(
  zod.string(),
  zod.unknown()
);

/**
 * @summary Update Auto Recipe Defaults
 */
export const UpdateAutoRecipeDefaultsParams = zod.object({
  tenant_name: zod.string(),
});

export const UpdateAutoRecipeDefaultsBody = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  parameters: zod.record(zod.string(), zod.unknown()),
});

export const UpdateAutoRecipeDefaultsResponse = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  effective: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  scrapchef: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  definition: zod.object({
    json_schema: zod
      .record(zod.string(), zod.unknown())
      .describe(
        "JSON Schema for editable values. Cross-field rules are checked by the server."
      ),
    sections: zod.array(
      zod.object({
        name: zod.string(),
        label: zod.string(),
        translation_key: zod.union([zod.string(), zod.null()]),
        fields: zod.array(
          zod.object({
            name: zod.string(),
            label: zod.string(),
            translation_key: zod.union([zod.string(), zod.null()]),
            unit: zod.union([
              zod
                .enum([
                  "mass",
                  "small_mass",
                  "cost",
                  "specific_cost",
                  "electrical_energy_consumption",
                  "mass_fraction",
                  "yield",
                  "volume",
                  "density",
                  "energy",
                  "specific_energy_cost",
                  "time",
                  "small_mass_fraction",
                ])
                .describe("Enum of quantities requiring different units"),
              zod.null(),
            ]),
            scopes: zod.array(zod.enum(["defaults", "search"])),
            widget: zod.enum([
              "scalar",
              "month",
              "time_intervals",
              "monthly_targets",
              "inventory_targets",
              "conditional_inventory_targets",
              "material_exclusivity",
              "material_limit_overrides",
              "steel_grades",
            ]),
            monthly_value_mode: zod.union([
              zod.enum(["independent", "carry_forward"]),
              zod.null(),
            ]),
            initial_value_from: zod.union([zod.string(), zod.null()]),
            price_material: zod.union([zod.string(), zod.null()]),
            help_text: zod.union([zod.string(), zod.null()]),
            help_translation_key: zod.union([zod.string(), zod.null()]),
          })
        ),
      })
    ),
    period_layout: zod.enum([
      "single_period",
      "deployable_sequence",
      "first_deployable_sequence",
      "schedule_forecast",
      "calendar_forecast",
      "weekly_forecast",
    ]),
  }),
});

/**
 * @summary Revert Auto Recipe Defaults
 */
export const RevertAutoRecipeDefaultsParams = zod.object({
  tenant_name: zod.string(),
});

export const RevertAutoRecipeDefaultsBody = zod.object({
  search_type: zod.enum([
    "charging",
    "charging_two_charge",
    "charging_three_charge",
    "charging_fresh_anode",
    "charging_wet",
    "charging_two_charge_wet",
    "medium_term_charging",
    "purchasing",
    "purchasing_unrestricted",
    "simulator",
  ]),
  effective_set_id: zod.union([zod.int(), zod.null()]),
  scrapchef_configuration_hash: zod.string(),
});

export const RevertAutoRecipeDefaultsResponse = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  effective: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  scrapchef: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    id: zod.union([zod.int(), zod.null()]),
    source: zod.enum(["scrapchef", "customer"]),
    parameters: zod.record(zod.string(), zod.unknown()),
  }),
  definition: zod.object({
    json_schema: zod
      .record(zod.string(), zod.unknown())
      .describe(
        "JSON Schema for editable values. Cross-field rules are checked by the server."
      ),
    sections: zod.array(
      zod.object({
        name: zod.string(),
        label: zod.string(),
        translation_key: zod.union([zod.string(), zod.null()]),
        fields: zod.array(
          zod.object({
            name: zod.string(),
            label: zod.string(),
            translation_key: zod.union([zod.string(), zod.null()]),
            unit: zod.union([
              zod
                .enum([
                  "mass",
                  "small_mass",
                  "cost",
                  "specific_cost",
                  "electrical_energy_consumption",
                  "mass_fraction",
                  "yield",
                  "volume",
                  "density",
                  "energy",
                  "specific_energy_cost",
                  "time",
                  "small_mass_fraction",
                ])
                .describe("Enum of quantities requiring different units"),
              zod.null(),
            ]),
            scopes: zod.array(zod.enum(["defaults", "search"])),
            widget: zod.enum([
              "scalar",
              "month",
              "time_intervals",
              "monthly_targets",
              "inventory_targets",
              "conditional_inventory_targets",
              "material_exclusivity",
              "material_limit_overrides",
              "steel_grades",
            ]),
            monthly_value_mode: zod.union([
              zod.enum(["independent", "carry_forward"]),
              zod.null(),
            ]),
            initial_value_from: zod.union([zod.string(), zod.null()]),
            price_material: zod.union([zod.string(), zod.null()]),
            help_text: zod.union([zod.string(), zod.null()]),
            help_translation_key: zod.union([zod.string(), zod.null()]),
          })
        ),
      })
    ),
    period_layout: zod.enum([
      "single_period",
      "deployable_sequence",
      "first_deployable_sequence",
      "schedule_forecast",
      "calendar_forecast",
      "weekly_forecast",
    ]),
  }),
});

/**
 * @summary Preview Auto Recipe Supply
 */
export const PreviewAutoRecipeSupplyParams = zod.object({
  tenant_name: zod.string(),
});

export const PreviewAutoRecipeSupplyQueryParams = zod.object({
  scope: zod.enum(["defaults", "search"]).optional(),
});

export const PreviewAutoRecipeSupplyBody = zod.object({
  context: zod.object({
    search_type: zod.enum([
      "charging",
      "charging_two_charge",
      "charging_three_charge",
      "charging_fresh_anode",
      "charging_wet",
      "charging_two_charge_wet",
      "medium_term_charging",
      "purchasing",
      "purchasing_unrestricted",
      "simulator",
    ]),
    effective_set_id: zod.union([zod.int(), zod.null()]),
    scrapchef_configuration_hash: zod.string(),
  }),
  parameters: zod.record(zod.string(), zod.unknown()),
  start_timestamp: zod.iso.datetime({ offset: true }),
  end_timestamp: zod.iso.datetime({ offset: true }),
});

export const previewAutoRecipeSupplyResponsePointsItemMassMin = 0;

export const previewAutoRecipeSupplyResponseTotalMassMin = 0;

export const PreviewAutoRecipeSupplyResponse = zod.object({
  points: zod.array(
    zod.object({
      timestamp: zod.iso.datetime({ offset: true }),
      mass: zod.number().min(previewAutoRecipeSupplyResponsePointsItemMassMin),
    })
  ),
  total_mass: zod.number().min(previewAutoRecipeSupplyResponseTotalMassMin),
});
